import {ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {MatDateFormats} from '@angular/material/core';
import {CalendarComponent, CalendarHeaderConfig} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {MIN_DATE} from '../../domain/constants';
import {customDateParsing, DateTimeAdapter, DateTimeFormat} from '../../infra/date-time.adapter';


@Component({
    selector: 'af-date-picker-field',
    templateUrl: './date-picker-field.component.html',
    styleUrl: './date-picker-field.component.scss',
    providers: [
        DateTimeAdapter,
    ],
    standalone: false
})
export class DatePickerFieldComponent implements OnInit {
    @ViewChild('datepicker') datePicker: CalendarComponent;
    @Input({required: true}) form: FormGroup<DateForm>;
    @Input({required: true}) supportedDateFormat: DateTimeFormat[];
    @Input({required: true}) dateFormats: MatDateFormats;
    @Input() displayFormat = 'DD.MM.YYYY';
    @Input() labelTranslationKey: string;
    @Input() showPickerActions: boolean;
    @Input() placeholderTranslationKey: string;
    @Input() minDate = MIN_DATE;
    @Input() iconPosition: 'left' | 'right' = 'right';
    @Input() isEndDate: boolean = false;
    @Input() maxDate: Date | null = new Date();
    Validators = Validators;
    headerConfig: CalendarHeaderConfig = {isCustomViewSwitchingEnabled: true};

    constructor(
        private dateAdapter: DateTimeAdapter,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        if (!this.form.contains('date')) {
            this.form.addControl('date', new FormControl(this.form.controls.date?.value));
        }
        if (this.showPickerActions && !this.form.contains('time')) {
            this.form.addControl('time', new FormControl(this.form.controls.time?.value));
        }

        this.dateAdapter.setFormats(this.supportedDateFormat, this.displayFormat);
        this.form.controls.time?.valueChanges
            ?.pipe(
                debounceTime(500),
                distinctUntilChanged(),
            )
            ?.subscribe(() => {
                this.datePicker._applyPendingSelection();
                this.formatDisplayedDate();
            });
        this.cdr.detectChanges();
    }

    selectDate(): void {
        this.datePicker._applyPendingSelection();
        this.formatDisplayedDate();
        this.datePicker.close();
    }

    dateInputChanged(event: any): void {
        const inputValue = event.target.value;
        const parsedDate = customDateParsing(inputValue, this.supportedDateFormat);
        if (parsedDate) {
            this.form.controls.date.setValue(parsedDate);
            this.syncTimeFieldFromDate(parsedDate);
        }
    }

    openPicker(): void {
        this.datePicker.open();
    }

    private formatDisplayedDate(): void {
        const dateValue = this.form.controls.date?.value;
        if (!this.form.controls.time || !dateValue) {
            return;
        }
        const date = dayjs(dateValue);
        let dateWithTime: Date;
        if (this.form.controls.time.value) {
            const [hours, minutes] = this.form.controls.time.value.split(':').map(Number);
            dateWithTime = date.hour(hours).minute(minutes).toDate();
        } else if (this.isEndDate) {
            dateWithTime = date.hour(23).minute(59).toDate();
            this.form.controls.time.setValue('23:59', {emitEvent: false});
        } else {
            dateWithTime = date.hour(0).minute(0).toDate();
        }
        this.form.controls.date.setValue(dateWithTime);
    }

    private syncTimeFieldFromDate(date: Date): void {
        if (!date) {
            return;
        }

        if (!this.form.controls.time) {
            return;
        }
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const timeString = `${hours}:${minutes}`;

        this.form.controls.time.setValue(timeString, {emitEvent: false});
    }
}

export type DateFormValue = ɵTypedOrUntyped<DateForm, ɵFormGroupRawValue<DateForm>, any>;

export interface DateForm {
    date: FormControl<Date>;
    time?: FormControl<string>;
}
